import { SURAHS } from "@/data/quran-meta";

/** Cumulative ayah offsets so we can map (surah, ayah) to the global 1..6236 index. */
const OFFSETS: number[] = (() => {
  const offsets: number[] = [0];
  let running = 0;
  for (const surah of SURAHS) {
    running += surah.ayahCount;
    offsets.push(running);
  }
  return offsets;
})();

export function globalAyahNumber(surah: number, ayah: number): number {
  return (OFFSETS[surah - 1] ?? 0) + ayah;
}

export type ReciterOption = {
  id: string;
  nameAr: string;
  nameEn: string;
  style: string;
  /** Bitrate folder that actually exists on the ayah-by-ayah CDN for this reciter. */
  ayahBitrate: 128 | 64;
  /** Full-surah recitation folder on mp3quran.net. */
  surahBase: string;
  /**
   * Folder name on the everyayah.com mirror. Used only as a fallback when the
   * primary ayah CDN returns an error, which it does intermittently.
   */
  mirrorFolder?: string;
};


/** Verified reciters. Each entry is checked against its audio source. */
export const RECITERS: ReciterOption[] = [
  {
    id: "ar.alafasy",
    nameAr: "مشاري العفاسي",
    nameEn: "Mishary Rashid Alafasy",
    style: "Murattal",
    ayahBitrate: 128,
    surahBase: "https://server8.mp3quran.net/afs",
    mirrorFolder: "Alafasy_128kbps",
  },
  {
    id: "ar.mahermuaiqly",
    nameAr: "ماهر المعيقلي",
    nameEn: "Maher Al Muaiqly",
    style: "Murattal",
    ayahBitrate: 128,
    surahBase: "https://server12.mp3quran.net/maher",
    mirrorFolder: "MaherAlMuaiqly128kbps",
  },
  {
    id: "ar.abdulbasitmurattal",
    nameAr: "عبد الباسط عبد الصمد",
    nameEn: "Abdul Basit Abdul Samad",
    style: "Murattal",
    ayahBitrate: 64,
    surahBase: "https://server7.mp3quran.net/basit",
    mirrorFolder: "Abdul_Basit_Murattal_64kbps",
  },
  {
    id: "ar.husary",
    nameAr: "محمود خليل الحصري",
    nameEn: "Mahmoud Khalil Al-Husary",
    style: "Murattal",
    ayahBitrate: 128,
    surahBase: "https://server13.mp3quran.net/husr",
    mirrorFolder: "Husary_128kbps",
  },
  {
    id: "ar.minshawi",
    nameAr: "محمد صديق المنشاوي",
    nameEn: "Mohamed Siddiq El-Minshawi",
    style: "Murattal",
    ayahBitrate: 128,
    surahBase: "https://server10.mp3quran.net/minsh",
    mirrorFolder: "Minshawy_Murattal_128kbps",
  },
  {
    id: "ar.abdurrahmaansudais",
    nameAr: "عبد الرحمن السديس",
    nameEn: "Abdurrahman As-Sudais",
    style: "Murattal",
    ayahBitrate: 64,
    surahBase: "https://server11.mp3quran.net/sds",
    mirrorFolder: "Abdurrahmaan_As-Sudais_64kbps",
  },
  {
    id: "ar.shaatree",
    nameAr: "أبو بكر الشاطري",
    nameEn: "Abu Bakr Ash-Shaatree",
    style: "Murattal",
    ayahBitrate: 128,
    surahBase: "https://server11.mp3quran.net/shatri",
    mirrorFolder: "Abu Bakr Ash-Shaatree_128kbps",
  },
  {
    id: "ar.hudhaify",
    nameAr: "علي الحذيفي",
    nameEn: "Ali Al-Hudhaify",
    style: "Murattal",
    ayahBitrate: 128,
    surahBase: "https://server9.mp3quran.net/hthfi",
    mirrorFolder: "Hudhaify_128kbps",
  },
  {
    id: "ar.ahmedajamy",
    nameAr: "أحمد بن علي العجمي",
    nameEn: "Ahmed ibn Ali Al-Ajamy",
    style: "Murattal",
    ayahBitrate: 128,
    surahBase: "https://server10.mp3quran.net/ajm",
  },
];

const AUDIO_BASE = "https://cdn.islamic.network/quran/audio";
const MIRROR_BASE = "https://everyayah.com/data";

export function reciterById(id: string): ReciterOption {
  return RECITERS.find((r) => r.id === id) ?? RECITERS[0]!;
}

/**
 * Upstream sources for one ayah, in the order the server should try them.
 * The primary CDN fails intermittently (502/403), so a mirror is always next.
 */
export function upstreamAyahUrls(reciterId: string, surah: number, ayah: number): string[] {
  const reciter = reciterById(reciterId);
  const urls = [`${AUDIO_BASE}/${reciter.ayahBitrate}/${reciter.id}/${globalAyahNumber(surah, ayah)}.mp3`];
  if (reciter.mirrorFolder) {
    const pad = (value: number) => String(value).padStart(3, "0");
    urls.push(`${MIRROR_BASE}/${encodeURIComponent(reciter.mirrorFolder)}/${pad(surah)}${pad(ayah)}.mp3`);
  }
  return urls;
}

/**
 * Where to look for one ayah, in order. Our own route is first: when the file is
 * already in our cloud store it answers with a redirect to the storage CDN, so
 * the bytes travel straight from the cloud to the listener for every reciter,
 * including any reciter uploaded later. No reciter list is hardcoded anywhere.
 */
export function ayahAudioSources(reciterId: string, surah: number, ayah: number): string[] {
  return [ayahAudioUrl(reciterId, surah, ayah)];
}


/**
 * Verified recitation of a single ayah, served from our own domain so a flaky
 * third-party CDN can never interrupt the reading. Never AI-generated.
 */
export function ayahAudioUrl(reciterId: string, surah: number, ayah: number): string {
  const reciter = reciterById(reciterId);
  return `/api/public/recitation/${reciter.id}/${surah}/${ayah}.mp3`;
}


/**
 * Verified recitation of a full surah, addressed on our own domain. The route
 * redirects to the source file, so the public address stays ours.
 */
export function surahAudioUrl(reciterId: string, surah: number): string {
  const reciter = reciterById(reciterId);
  return `/api/public/recitation/${reciter.id}/${surah}.mp3`;
}

/** The upstream file a full-surah address redirects to. */
export function upstreamSurahUrl(reciterId: string, surah: number): string {
  const reciter = reciterById(reciterId);
  return `${reciter.surahBase}/${String(surah).padStart(3, "0")}.mp3`;
}

